import React, { useState, useEffect, useContext } from 'react';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Plus, Edit, Trash2, ShoppingCart } from 'lucide-react';




function ProductManagement() {
	const { hasPermission } = useAuth();
    const { user, isAdmin } = useAuth();
    const [products, setProducts] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        brand: '',
        price: '',
        short_description: '',
        long_description: '',
        featured: false
    });
    const [selectedImage, setSelectedImage] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
		try {
			// Remove the API_BASE_URL reference
			const response = await apiClient.get(`/products`);
			setProducts(response.data);
		} catch (error) {
			console.error('Error fetching products:', error);
		}
	};

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleCheckboxChange = (checked) => {
        setFormData({ ...formData, featured: checked });
    };

    const handleImageChange = (e) => {
        setSelectedImage(e.target.files[0]);
    };

    const handleCreateProduct = async () => {
        setLoading(true);
        
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('brand', formData.brand);
            formDataToSend.append('price', formData.price);
            formDataToSend.append('short_description', formData.short_description);
            formDataToSend.append('long_description', formData.long_description);
            formDataToSend.append('featured', formData.featured);
            if (selectedImage) {
                formDataToSend.append('image', selectedImage);
            }

            await apiClient.post(`/products`, formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                }
            });
            
            fetchProducts();
            resetForm();
        } catch (error) {
            console.error('Error creating product:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateProduct = async () => {
        setLoading(true);
        
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('brand', formData.brand);
            formDataToSend.append('price', formData.price);
            formDataToSend.append('short_description', formData.short_description);
            formDataToSend.append('long_description', formData.long_description);
            formDataToSend.append('featured', formData.featured);
            if (selectedImage) {
                formDataToSend.append('image', selectedImage);
            }

            await apiClient.put(`/products/${editingProduct.product_id}`, formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                }
            });
            
            fetchProducts();
            resetForm();
        } catch (error) {
            console.error('Error updating product:', error);
        } finally {
            setLoading(false);
        }
    };

	const handleDeleteProduct = async (productId) => {
	  // Add validation for productId
	  if (!productId || !window.confirm('Are you sure you want to delete this product?')) return;
	  
	  try {
		await apiClient.delete(`/products/${productId}`, {
		  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
		});
		fetchProducts();
	  } catch (error) {
		console.error('Error deleting product:', error);
		// Add error handling
		alert('Failed to delete product. Please try again.');
	  }
	};

    const resetForm = () => {
        setFormData({
            name: '',
            brand: '',
            price: '',
            short_description: '',
            long_description: '',
            featured: false
        });
        setSelectedImage(null);
        setEditingProduct(null);
        setShowForm(false);
    };

    const openEditModal = (product) => {
        setEditingProduct(product);
        setFormData({
            name: product.name,
            brand: product.brand,
            price: product.price,
            short_description: product.short_description,
            long_description: product.long_description,
            featured: product.featured
        });
        setShowForm(true);
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex justify-between items-center">
                    <CardTitle>Product Management</CardTitle>
                    {hasPermission('manage_products') && (
					  <Button onClick={() => setShowForm(true)}>
						<Plus className="mr-2" size={16} /> Add Product
					  </Button>
					)}
                </div>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {products.map(product => (
                        <Card key={product.product_id} className="relative">
                            <div className="absolute top-2 right-2 flex space-x-2">
                                <Button 
                                    size="icon" 
                                    variant="outline"
                                    onClick={() => openEditModal(product)}
                                >
                                    <Edit size={16} />
                                </Button>
								<Button 
								  size="icon" 
								  variant="destructive"
								  onClick={() => handleDeleteProduct(product.product_id)}
								  disabled={!hasPermission('manage_products')}
								  title={!hasPermission('manage_products') ? 'You need manage_products permission' : 'Delete product'}
								>
								  <Trash2 size={16} />
								</Button>
                            </div>
                            
                            <CardContent className="p-4">
                                <div className="aspect-square bg-gray-100 rounded-lg mb-4 overflow-hidden">
                                    {product.image_url ? (
                                        <img 
                                            src={product.image_url?.startsWith('http') ? product.image_url : `/api${product.image_url}`} 
                                            alt={product.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <ShoppingCart size={48} className="text-gray-400" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center justify-between mb-2">
                                    <Badge className="bg-orange-100 text-orange-800">{product.brand}</Badge>
                                    {product.featured && (
                                        <Badge className="bg-green-100 text-green-800">Featured</Badge>
                                    )}
                                </div>
                                <h3 className="font-semibold text-lg mb-2">{product.name}</h3>
                                <p className="text-gray-600 text-sm mb-2">{product.short_description}</p>
                                <p className="text-2xl font-bold text-orange-600">₹{product.price}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </CardContent>

            {/* Product Form Modal */}
            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {editingProduct ? 'Edit Product' : 'Add New Product'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="name">Product Name</Label>
                                <Input
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="brand">Brand</Label>
                                <Input
                                    id="brand"
                                    name="brand"
                                    value={formData.brand}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="price">Price (₹)</Label>
                            <Input
                                id="price"
                                name="price"
                                type="number"
                                step="0.01"
                                value={formData.price}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div>
                            <Label htmlFor="short_description">Short Description</Label>
                            <Input
                                id="short_description"
                                name="short_description"
                                value={formData.short_description}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div>
                            <Label htmlFor="long_description">Long Description</Label>
                            <Textarea
                                id="long_description"
                                name="long_description"
                                value={formData.long_description}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div>
                            <Label htmlFor="image">Product Image</Label>
                            <Input
                                id="image"
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                required={!editingProduct}
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="featured"
                                checked={formData.featured}
                                onCheckedChange={handleCheckboxChange}
                            />
                            <Label htmlFor="featured">Featured on homepage</Label>
                        </div>

                        <div className="flex justify-end space-x-4">
                            <Button 
                                onClick={editingProduct ? handleUpdateProduct : handleCreateProduct}
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Save Product'}
                            </Button>
                            <Button variant="outline" onClick={resetForm}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

export default ProductManagement;